import { Card, CardActions, CardContent, Stack } from '@mui/material';
import type { RouteComponentProps } from '@reach/router';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { OperationAdminControl } from '@components/Admin';
import type { Editor } from '@components/Admin/Common';
import { FormikTextField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import { useAdminAddPolicyToOperation } from 'generated/admin.generated';

const messages = defineMessages({
  name: {
    id: 'admin.operations.name',
    defaultMessage: 'Name',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
});

type FormValues = {
  operation: string;
  policy_id: string;
};

const NewOperationForm: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const [createOperation, { loading: createOperationLoading }] =
    useAdminAddPolicyToOperation();

  const loading = createOperationLoading;

  const validationSchema: Yup.Schema<FormValues> = Yup.object().shape({
    operation: Yup.string()
      .max(50)
      .matches(/^[a-z][a-zA-Z]*$/)
      .required(formatMessage(messages.required)),
    policy_id: Yup.string().required(formatMessage(messages.required)),
  });

  const initialValues: FormValues = {
    operation: '',
    policy_id: '',
  };

  async function handleSubmit(
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>
  ) {
    await createOperation({
      variables: {
        input: {
          operation: values.operation,
          policy_id: values.policy_id,
        },
      },
    });
    resetForm();
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, isSubmitting }) => {
        const handlePolicySelect = (option: Editor.EditorOption) => {
          setFieldValue('policy_id', option.id);
        };

        return (
          <Form>
            <Stack spacing={1}>
              <Card>
                <CardContent>
                  <FormikTextField
                    name="operation"
                    label={formatMessage(messages.name)}
                  />
                </CardContent>
              </Card>
              <OperationAdminControl.PolicyEditor
                onPolicySelect={handlePolicySelect}
                loading={loading}
              />
              <Card>
                <CardActions>
                  <RoundedButton
                    disabled={isSubmitting || loading}
                    type="submit"
                  >
                    {formatMessage(messages.submit)}
                  </RoundedButton>
                </CardActions>
              </Card>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};

export default NewOperationForm;
